import {
  disconnectGoogleDrive,
  getGoogleDriveConnectUrl,
  getGoogleDriveStatus,
  setGoogleDriveAuthorizedFiles,
} from "@/modules/settings/requests/google-drive";
import { ZButton } from "@/shared/components/button";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useGoogleDrivePicker } from "./useGoogleDrivePicker";

const GOOGLE_DRIVE_STATUS_QUERY_KEY = ["google-drive", "status"] as const;

function googleDriveErrorToMessage(errorCode: string): string {
  const map: Record<string, string> = {
    invalid_state: "Invalid state. Please try connecting again.",
    exchange_failed: "Authorization exchange failed. Please try again.",
  };
  return map[errorCode] ?? `Connection failed: ${errorCode}`;
}

function StatusCardSkeleton() {
  return (
    <div className="p-4 bg-[#FAFAFA] border border-[#E4E4E7] rounded-xl flex flex-col gap-4 animate-pulse">
      <div className="flex flex-col gap-2">
        <div className="h-4 w-28 rounded bg-[#E4E4E7]" />
        <div className="h-3 w-64 rounded bg-[#E4E4E7]" />
      </div>
      <div className="flex gap-2">
        <div className="h-8 w-32 rounded bg-[#E4E4E7]" />
        <div className="h-8 w-32 rounded bg-[#E4E4E7]" />
      </div>
    </div>
  );
}

export default function GoogleDriveIntegrationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const picker = useGoogleDrivePicker();
  const [oauthBanner, setOauthBanner] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // const handleConnectGoogleDrive = () => {
  //   window.location.href = `${googleDriveApiBaseURL}/v0/integrations/google-drive/connect/redirect`;
  // };

  const handleConnectGoogleDrive = async () => {
    try {
      const { authorize_url } = await getGoogleDriveConnectUrl();
      window.location.href = authorize_url;
    } catch (err) {
      addNotification({
        type: "error",
        title: "Failed to connect Google Drive",
        message: axios.isAxiosError(err)
          ? String(err.response?.data?.detail ?? err.message)
          : err instanceof Error
            ? err.message
            : "Unable to get Google Drive connect URL. Please try again.",
      });
    }
  };

  // Parse OAuth callback query on mount
  useEffect(() => {
    const googleDrive = searchParams.get("google_drive");
    const error = searchParams.get("error");
    if (googleDrive === "connected") {
      setOauthBanner({ type: "success", message: "Google Drive connected successfully" });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("google_drive");
          next.delete("error");
          return next;
        },
        { replace: true },
      );
    } else if (googleDrive === "error" && error) {
      setOauthBanner({
        type: "error",
        message: googleDriveErrorToMessage(error),
      });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("google_drive");
          next.delete("error");
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const { data: status, isPending: statusPending } = useQuery({
    queryKey: GOOGLE_DRIVE_STATUS_QUERY_KEY,
    queryFn: getGoogleDriveStatus,
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectGoogleDrive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GOOGLE_DRIVE_STATUS_QUERY_KEY });
      addNotification({
        type: "success",
        title: "Disconnected",
        message: "Google Drive disconnected successfully.",
      });
    },
    onError: (err) => {
      addNotification({
        type: "error",
        title: "Disconnect failed",
        message: axios.isAxiosError(err)
          ? String(err.response?.data?.detail ?? err.message)
          : "Disconnect failed, please try again later.",
      });
    },
  });

  const setAuthorizedFilesMutation = useMutation({
    mutationFn: setGoogleDriveAuthorizedFiles,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GOOGLE_DRIVE_STATUS_QUERY_KEY });
      addNotification({
        type: "success",
        title: "Authorized files saved",
        message: "Selected files saved successfully.",
      });
    },
    onError: (err) => {
      addNotification({
        type: "error",
        title: "Failed to save authorized files",
        message: axios.isAxiosError(err)
          ? String(err.response?.data?.detail ?? err.message)
          : "Please try again later.",
      });
    },
  });

  const connected = status?.connected === true;
  const selectedFolder = status?.selected_folder;

  const handleChooseFolder = async () => {
    try {
      const result = await picker.pickFolderThenFiles();
      if (!result) return;
      if (!result.docs || result.docs.length === 0) {
        console.log("[Google Drive] Folder selected but no files chosen; authorized files not saved.", {
          folderId: result.folderId,
        });
        addNotification({
          type: "error",
          title: "Select at least one file",
          message: "Please select at least one file. Authorized files were not saved.",
        });
        return;
      }
      setAuthorizedFilesMutation.mutate({
        authorized_resources: [
          {
            folder_id: result.folderId,
            file_ids: result.docs.map((d) => d.id),
          },
        ],
        mode: "SELECTED_FILES",
      });
      addNotification({
        type: "success",
        title: "Authorized files selected",
        message: `Saved ${result.docs.length} file(s) from Drive.`,
      });
    } catch (err) {
      addNotification({
        type: "error",
        title: "Picker failed",
        message: err instanceof Error ? err.message : "Unable to open Google Picker.",
      });
    }
  };

  const handlePickFiles = async () => {
    if (!selectedFolder?.id) return;
    try {
      const docs = await picker.pickFilesInFolder(selectedFolder.id);
      if (!docs) return;
      addNotification({
        type: "success",
        title: "Files selected",
        message: `Selected ${docs.length} file(s) from Drive.`,
      });
    } catch (err) {
      addNotification({
        type: "error",
        title: "Picker failed",
        message: err instanceof Error ? err.message : "Unable to open Google Picker.",
      });
    }
  };

  return (
    <div className="p-6 flex flex-col gap-6 max-w-2xl">
      {oauthBanner && (
        <div
          className={
            oauthBanner.type === "success"
              ? "p-4 rounded-lg bg-green-50 border border-green-200 text-green-800"
              : "p-4 rounded-lg bg-red-50 border border-red-200 text-red-800"
          }
        >
          {oauthBanner.message}
        </div>
      )}

      <div className="flex items-start gap-4">
        <img src="/google-drive-logo.svg" alt="Google Drive" className="w-12 h-12 rounded-lg object-contain shrink-0" />
        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-medium leading-5 text-[#171717]">Google Drive Integration</h4>
          <p className="text-sm text-[#777780]">Connect Google Drive to import files for assessments.</p>
        </div>
      </div>

      {statusPending ? (
        <StatusCardSkeleton />
      ) : (
        <div className="p-4 bg-[#FAFAFA] border border-[#E4E4E7] rounded-xl flex flex-col gap-4">
          {connected ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#171717]">Connected</span>
                {status?.user_email && <span className="text-sm text-[#717179]">Account: {status.user_email}</span>}
                {selectedFolder?.id && (
                  <span className="text-sm text-[#717179]">Folder: {selectedFolder.name ?? selectedFolder.id}</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  {/* <ZButton
                    color="default"
                    variant="bordered"
                    className="w-fit"
                    isDisabled={setAuthorizedFilesMutation.isPending}
                    isLoading={setAuthorizedFilesMutation.isPending}
                    onClick={handleChooseFolder}
                  >
                    Select folders
                  </ZButton> */}
                  {/* <ZButton color="primary" className="w-fit" isDisabled={!selectedFolder?.id} onClick={handlePickFiles}>
                    Import files
                  </ZButton> */}
                  <ZButton
                    color="default"
                    variant="bordered"
                    className="w-fit"
                    isDisabled={disconnectMutation.isPending}
                    isLoading={disconnectMutation.isPending}
                    onClick={() => disconnectMutation.mutate()}
                  >
                    Disconnect
                  </ZButton>
                </div>
                {/* <p className="text-xs text-[#717179]">
                  Only selected folders will be used for assessments(optional).
                </p> */}
              </div>
            </>
          ) : (
            <>
              <span className="text-sm text-[#717179]">Not connected</span>
              <ZButton color="primary" className="w-fit" onClick={handleConnectGoogleDrive}>
                Connect Google Drive
              </ZButton>
            </>
          )}
        </div>
      )}

      <div className="pt-2">
        <Link
          to={`/${ROUTES.SETTINGS}?tab=integrations`}
          className="text-sm text-primary hover:underline"
        >
          ← Back to integrations list
        </Link>
      </div>
    </div>
  );
}
